import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as DeliveryNoteApi from '../../../../../../../../services/api/deliveryNote';
import * as ConcreteBatchesApi from '../../../../../../../../services/api/concreteBatches';
import useNormativeApi from './useNormativeApi';
import {
  getDeliveryNote,
  updateDeliveryNote,
} from '../../../../../../../../services/redux/deliveryNotes/actions';
import {
  createConcreteBatch,
  deleteConcreteBatch,
} from '../../../../../../../../services/redux/concreteBatch/actions';
import { useOutletContext } from 'react-router-dom';
import { getNextBatchCode } from '../index.utils';
import { addConcreteBatchToTag } from '../../../../../../../../services/api/tags';

const useLotification = () => {
  const dispatch = useDispatch();
  const { project } = useOutletContext();
  const { updateDataScript } = useNormativeApi();

  const unBatchItem = useCallback(
    (deliveryNote, formValues, isLotification) => {
      return new Promise((resolve, reject) => {
        const initialBatch = deliveryNote.concrete_batch_data?.id || null;

        if (initialBatch) {
          deliveryNote.concrete_batch = null;
        }

        dispatch(
          updateDeliveryNote({
            deliveryNote: { ...deliveryNote, ...formValues },
            callback: async () => {
              try {
                const continueAfterDelete = async () => {
                  if (
                    initialBatch === null ||
                    initialBatch !== formValues.batch_code
                  ) {
                    await updateDataScript(
                      isLotification ?? false,
                      deliveryNote.id,
                      null,
                      initialBatch || null,
                    );
                  }
                  resolve();
                };

                if (initialBatch) {
                  const associatedDeliveryNotes =
                    await DeliveryNoteApi.listDeliveryNote({
                      filters: { concrete_batch: initialBatch },
                    });

                  if (
                    associatedDeliveryNotes.data &&
                    !associatedDeliveryNotes.data.total
                  ) {
                    dispatch(
                      deleteConcreteBatch({
                        id: initialBatch,
                        callback: continueAfterDelete,
                      }),
                    );
                  } else {
                    await continueAfterDelete();
                  }
                } else {
                  await continueAfterDelete();
                }
              } catch (error) {
                reject(error);
              }
            },
          }),
        );
      });
    },
    [dispatch, updateDataScript],
  );

  const createNewConcreteBatch = useCallback(
    (deliveryNote, formValues, isLotification) => {
      return new Promise(async (resolve, reject) => {
        const initialBatch = deliveryNote.concrete_batch_data?.id || null;

        const {
          data: { data: allConcreteBatches },
        } = await ConcreteBatchesApi.getConcreteBatches({
          project: project.id_project,
          size: 1000,
          filters: {},
        });

        const uniqueBatchCodes = Array.from(
          // eslint-disable-next-line react/prop-types
          new Set(allConcreteBatches.map((item) => item.batch_code)),
        );

        const newBatchCode = uniqueBatchCodes.length
          ? getNextBatchCode(uniqueBatchCodes)
          : 1;

        if (project.version === 1) {
          dispatch(
            createConcreteBatch({
              concreteBatch: {
                batch_code: newBatchCode,
                project: parseInt(project.id_project, 10),
              },
              callback: (data) => {
                dispatch(
                  updateDeliveryNote({
                    deliveryNote: {
                      ...deliveryNote,
                      ...formValues,
                      concrete_batch: data.id,
                    },
                    callback: async () => {
                      const continueAfterDelete = async () => {
                        if (
                          (deliveryNote.concrete_batch === null &&
                            newBatchCode) ||
                          deliveryNote.concrete_batch !== newBatchCode
                        ) {
                          await updateDataScript(
                            isLotification ?? false,
                            deliveryNote.id,
                            newBatchCode,
                            deliveryNote.concrete_batch || null,
                          )
                            .then(() => {
                              dispatch(
                                getDeliveryNote({
                                  id: deliveryNote.id,
                                }),
                              );
                              resolve();
                            })
                            .catch(reject);
                        } else {
                          resolve();
                        }
                      };

                      const associatedDeliveryNotes =
                        await DeliveryNoteApi.listDeliveryNote({
                          filters: { concrete_batch: initialBatch },
                        });

                      if (associatedDeliveryNotes.data) {
                        if (!associatedDeliveryNotes.data.total) {
                          dispatch(
                            deleteConcreteBatch({
                              id: initialBatch,
                              callback: continueAfterDelete,
                            }),
                          );
                        } else {
                          await continueAfterDelete();
                        }
                      } else {
                        await continueAfterDelete();
                      }
                    },
                  }),
                );
              },
            }),
          );
        } else {
          addConcreteBatchToTag(
            formValues.tag,
            newBatchCode,
            project.id_project,
          ).then(({ data }) => {
            dispatch(
              updateDeliveryNote({
                deliveryNote: {
                  ...deliveryNote,
                  ...formValues,
                  concrete_batch: data.id_concrete_batch,
                },
                callback: async () => {
                  const continueAfterDelete = async () => {
                    if (
                      (deliveryNote.concrete_batch === null && newBatchCode) ||
                      deliveryNote.concrete_batch !== newBatchCode
                    ) {
                      await updateDataScript(
                        isLotification ?? false,
                        deliveryNote.id,
                        newBatchCode,
                        deliveryNote.concrete_batch || null,
                      )
                        .then(() => {
                          dispatch(
                            getDeliveryNote({
                              id: deliveryNote.id,
                            }),
                          );
                          resolve();
                        })
                        .catch(reject);
                    } else {
                      resolve();
                    }
                  };

                  const associatedDeliveryNotes =
                    await DeliveryNoteApi.listDeliveryNote({
                      filters: { concrete_batch: initialBatch },
                    });

                  if (associatedDeliveryNotes.data) {
                    if (!associatedDeliveryNotes.data.total) {
                      dispatch(
                        deleteConcreteBatch({
                          id: initialBatch,
                          callback: continueAfterDelete,
                        }),
                      );
                    } else {
                      await continueAfterDelete();
                    }
                  } else {
                    await continueAfterDelete();
                  }
                },
              }),
            );
          });
        }
      });
    },
    [dispatch, project.id_project, project.version, updateDataScript],
  );

  const changeToAnExistingBatch = useCallback(
    (deliveryNote, formValues, isLotification) => {
      return new Promise((resolve, reject) => {
        const initialBatch = deliveryNote.concrete_batch;

        dispatch(
          updateDeliveryNote({
            deliveryNote: {
              ...deliveryNote,
              ...formValues,
              concrete_batch: formValues.batch_code,
            },
            callback: async () => {
              try {
                const continueAfterDelete = async () => {
                  if (!initialBatch || initialBatch !== formValues.batch_code) {
                    await updateDataScript(
                      isLotification ?? false,
                      deliveryNote.id,
                      formValues.batch_code,
                      initialBatch || null,
                    ).then(() => {
                      dispatch(
                        getDeliveryNote({
                          id: deliveryNote.id,
                        }),
                      );
                      resolve();
                    });
                  } else {
                    resolve();
                  }
                };

                if (initialBatch) {
                  const associatedDeliveryNotes =
                    await DeliveryNoteApi.listDeliveryNote({
                      filters: { concrete_batch: initialBatch },
                    });

                  if (
                    associatedDeliveryNotes.data &&
                    !associatedDeliveryNotes.data.total
                  ) {
                    dispatch(
                      deleteConcreteBatch({
                        id: initialBatch,
                        callback: continueAfterDelete,
                      }),
                    );
                  } else {
                    await continueAfterDelete();
                  }
                } else {
                  await continueAfterDelete();
                }
              } catch (error) {
                reject(error);
              }
            },
          }),
        );
      });
    },
    [dispatch, updateDataScript],
  );

  return {
    unBatchItem,
    createNewConcreteBatch,
    changeToAnExistingBatch,
  };
};

export default useLotification;
